/**
 * metaLeadGen/components/ComboChart.tsx
 *
 * Reusable combo chart: 1-2 volume bars + 1 efficiency line + dotted average.
 *
 * ARCHITECTURE CONTRACT:
 *   Pure visualization. All data and averages come from getDailyMetrics().
 *   Zero metric calculations.
 *
 * Axis layout:
 *   Left Y-axis   → bar1 scale (always visible)
 *   Right Y-axis  → bar2 scale when bar2 present, else line scale (always visible)
 *   Hidden axis   → line scale when bar2 present (independent from bar/right scale)
 *
 * Visual rules:
 *   - type="linear" → sharp straight segments, no smoothing
 *   - Dotted ReferenceLine on line average only
 *   - Grouped bars (barCategoryGap + barGap)
 *   - No hardcoded hex colors — branding only
 */

import { useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import type { DailyMetricRow } from "../dailyMetrics";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export interface BarMetricConfig {
  /** Key on DailyMetricRow (must be a numeric field) */
  dataKey: string;
  label: string;
  color: string;
  formatter: (v: number) => string;
  axisFormatter: (v: number) => string;
}

export interface LineMetricConfig {
  /** Key on DailyMetricRow (must be a numeric field) */
  dataKey: string;
  label: string;
  color: string;
  /** Pre-computed period average from ChartMeta — never calculated here. */
  average: number;
  formatter: (v: number) => string;
  axisFormatter: (v: number) => string;
}

export interface ComboChartProps {
  title: string;
  data: DailyMetricRow[];
  bar1: BarMetricConfig;
  /** Optional second bar — uses a hidden axis so its scale doesn't distort bar1. */
  bar2?: BarMetricConfig;
  line: LineMetricConfig;
  loading?: boolean;
}

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const CHART_HEIGHT = 280;

// ─────────────────────────────────────────────
// SAFE VALUE READER
// ─────────────────────────────────────────────
function getRowVal(row: DailyMetricRow, key: string): number {
  const v = (row as Record<string, unknown>)[key];
  return typeof v === "number" ? v : 0;
}

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────
interface TooltipPayloadItem { dataKey: string; value: number }
interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  bar1: BarMetricConfig;
  bar2?: BarMetricConfig;
  line: LineMetricConfig;
}

function ChartTooltip({ active, payload, label, bar1, bar2, line }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const get = (key: string) => payload.find(p => p.dataKey === key)?.value ?? 0;

  return (
    <div className="bg-popover border border-border shadow-xl rounded-xl p-3 min-w-[170px] text-xs">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">
        {label ?? ""}
      </p>

      {/* Bar1 */}
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: bar1.color }} />
          <span className="text-muted-foreground">{bar1.label}</span>
        </div>
        <span className="font-semibold text-foreground">{bar1.formatter(get(bar1.dataKey))}</span>
      </div>

      {/* Bar2 (optional) */}
      {bar2 && (
        <div className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: bar2.color }} />
            <span className="text-muted-foreground">{bar2.label}</span>
          </div>
          <span className="font-semibold text-foreground">{bar2.formatter(get(bar2.dataKey))}</span>
        </div>
      )}

      {/* Line */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1.5">
          <span className="relative flex-shrink-0 w-5 h-3 flex items-center">
            <span
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] rounded-full"
              style={{ backgroundColor: line.color }}
            />
            <span
              className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full border-2"
              style={{ backgroundColor: line.color, borderColor: "var(--popover)" }}
            />
          </span>
          <span className="text-muted-foreground">{line.label}</span>
        </div>
        <span className="font-semibold text-foreground">{line.formatter(get(line.dataKey))}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// LEGEND
// ─────────────────────────────────────────────
function ChartLegend({
  bar1,
  bar2,
  line,
}: {
  bar1: BarMetricConfig;
  bar2?: BarMetricConfig;
  line: LineMetricConfig;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: bar1.color }} />
        <span className="text-[10px] font-medium text-muted-foreground">{bar1.label}</span>
      </div>

      {bar2 && (
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: bar2.color }} />
          <span className="text-[10px] font-medium text-muted-foreground">{bar2.label}</span>
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <span className="relative flex-shrink-0 w-6 h-3 flex items-center">
          <span
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] rounded-full"
            style={{ backgroundColor: line.color }}
          />
          <span
            className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full border-2"
            style={{ backgroundColor: line.color, borderColor: "var(--card)" }}
          />
        </span>
        <span className="text-[10px] font-medium text-muted-foreground">{line.label}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────
function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export function ComboChart({
  title,
  data,
  bar1,
  bar2,
  line,
  loading = false,
}: ComboChartProps) {
  const hasData = data.length > 0;
  const allZero = hasData && data.every(r =>
    getRowVal(r, bar1.dataKey) === 0 &&
    (bar2 ? getRowVal(r, bar2.dataKey) === 0 : true) &&
    getRowVal(r, line.dataKey) === 0
  );

  const xAxisInterval = useMemo(
    () => (data.length <= 14 ? 0 : Math.ceil(data.length / 10) - 1),
    [data.length]
  );

  // When bar2 exists: right axis shows bar2 scale; line gets its own hidden axis.
  // When no bar2: right axis shows line scale.
  const rightAxisFormatter = bar2 ? bar2.axisFormatter : line.axisFormatter;
  const lineAxisId = bar2 ? "lineHidden" : "right";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-card rounded-xl border border-border p-5"
    >
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {!loading && hasData && !allZero && (
          <ChartLegend bar1={bar1} bar2={bar2} line={line} />
        )}
      </div>

      {/* Chart body */}
      <div style={{ height: CHART_HEIGHT }}>
        {loading ? (
          <div className="flex flex-col gap-2 h-full animate-pulse">
            <div className="h-4 w-32 rounded bg-muted" />
            <div className="flex-1 rounded-lg bg-muted/50" />
          </div>
        ) : !hasData || allZero ? (
          <EmptyState
            message={
              allZero
                ? "All values are zero for selected filters"
                : "No data for selected filters"
            }
          />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 8, right: 56, bottom: 0, left: 8 }}
              barCategoryGap="18%"
              barGap={2}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="currentColor"
                strokeOpacity={0.06}
              />

              <XAxis
                dataKey="display.date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "currentColor", opacity: 0.45 }}
                dy={6}
                interval={xAxisInterval}
              />

              {/* Left axis: bar1 scale */}
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "currentColor", opacity: 0.45 }}
                tickFormatter={bar1.axisFormatter}
                domain={[0, (dataMax: number) => dataMax > 0 ? Math.ceil(dataMax * 1.2) : 10]}
                tickCount={5}
                allowDecimals={false}
                width={52}
              />

              {/* Right axis: bar2 scale when present, else line scale */}
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "currentColor", opacity: 0.45 }}
                tickFormatter={rightAxisFormatter}
                domain={[0, (dataMax: number) => dataMax > 0 ? Math.ceil(dataMax * 1.2) : 10]}
                tickCount={5}
                allowDecimals={false}
                width={44}
              />

              {/* Hidden axis for line when bar2 is present (independent scale) */}
              {bar2 && (
                <YAxis
                  yAxisId="lineHidden"
                  hide={true}
                  width={0}
                  axisLine={false}
                  tickLine={false}
                />
              )}

              <Tooltip
                content={props => (
                  <ChartTooltip {...props} bar1={bar1} bar2={bar2} line={line} />
                )}
                cursor={{ fill: "currentColor", fillOpacity: 0.04 }}
              />

              {/* ── Bars ─────────────────────────────────────────────────── */}
              <Bar
                yAxisId="left"
                dataKey={bar1.dataKey}
                fill={bar1.color}
                fillOpacity={0.85}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
                isAnimationActive={true}
                animationDuration={800}
                animationEasing="ease-out"
              />

              {bar2 && (
                <Bar
                  yAxisId="right"
                  dataKey={bar2.dataKey}
                  fill={bar2.color}
                  fillOpacity={0.85}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={40}
                  isAnimationActive={true}
                  animationDuration={900}
                  animationEasing="ease-out"
                />
              )}

              {/* ── Average reference line (dotted, line metric only) ─────── */}
              {line.average > 0 && (
                <ReferenceLine
                  yAxisId={lineAxisId}
                  y={line.average}
                  stroke={line.color}
                  strokeDasharray="5 4"
                  strokeOpacity={0.45}
                  strokeWidth={1.5}
                  label={{
                    value: line.formatter(line.average),
                    position: "right",
                    fontSize: 9,
                    fill: line.color,
                    opacity: 0.7,
                  }}
                />
              )}

              {/* ── Efficiency line (sharp point-to-point, no smoothing) ─── */}
              <Line
                yAxisId={lineAxisId}
                type="linear"
                dataKey={line.dataKey}
                stroke={line.color}
                strokeWidth={2.5}
                dot={{ r: 3, fill: line.color, strokeWidth: 0 }}
                activeDot={{
                  r: 5,
                  fill: line.color,
                  stroke: "var(--background)",
                  strokeWidth: 2,
                }}
                isAnimationActive={true}
                animationDuration={1000}
                animationEasing="ease-out"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </motion.div>
  );
}
